import { useState, useEffect, useRef } from 'react';

import { Box, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as EditOutlinedIcon } from 'assets/images/icons/icn_edit_light.svg';
import { ReactComponent as ClearIcon } from 'assets/images/splashPage/btn_clear_icon.svg';

import StyleEditorPopover from './StyleEditorPopover';

const EditableBlock = ({
  children,
  editable = true,
  editorTitle,
  editorFields = [],
  style = {},
  onStyleChange,
  blockSx = {},
  onSelect,
  isSelected = false,
  showClearIcon = false,
  onClearIconClick = () => {},
}) => {
  const theme = useTheme();

  const [isHovered, setIsHovered] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const editIconRef = useRef(null);
  const clearIconRef = useRef(null);

  const showOutline = editable;
  const showEditIcon = editable && (isHovered || isSelected);

  // Background, Logo, Success Icon
  const iconPositionStyles = editorTitle === 'Background' ? { left: 40, top: 20 } : { right: -12, top: -12 };
  const clearIconPositionStyles = editorTitle === 'Background' ? { left: 80, top: 20 } : { right: 26, top: -12 };

  useEffect(() => {
    const handler = () => setIsPopoverOpen(false);
    window.addEventListener('editableblock:closeAll', handler);
    return () => window.removeEventListener('editableblock:closeAll', handler);
  }, []);

  if (!editable) {
    return <Box sx={blockSx}>{children}</Box>;
  }

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={e => {
        // ignore clicks originating from inside the style editor panel or its portal picker
        try {
          if (e.target && e.target.closest && e.target.closest('[data-style-editor-panel]')) return;
          if (window.__styleEditorActivePicker && window.__styleEditorActivePicker.contains && window.__styleEditorActivePicker.contains(e.target)) return;
        } catch (err) {}
        window.dispatchEvent(new CustomEvent('editableblock:closeAll'));
        if (onSelect) onSelect(e);
      }}
      sx={{
        position: 'relative',
        cursor: 'auto',
        ...blockSx,
      }}
    >
      <Box
        sx={{
          width: '100%',
          textAlign: 'center',
          border: isHovered ? `1px solid ${theme.palette.primary.main}` : showOutline ? `1px dashed ${theme.palette.primary.main}` : '1px dashed transparent',
          backgroundColor: isHovered ? 'rgba(227,242,255,0.9)' : 'transparent',
          transition: 'border-color 160ms ease, background-color 160ms ease',
          padding: '6px',
        }}
      >
        {children}
      </Box>

      {/* Clear icon */}
      {showClearIcon && (editorTitle === 'Background' || editorTitle === 'Logo' || editorTitle === 'Success Icon') && (
        <IconButton
          ref={clearIconRef}
          size="small"
          onClick={e => {
            e.stopPropagation();
            onClearIconClick();
          }}
          sx={{
            position: 'absolute',
            ...clearIconPositionStyles,
            width: 28,
            height: 28,
            zIndex: 10,
            borderRadius: '50%',
            backgroundColor: '#E6185A',
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
            transform: 'scale(1)',
            opacity: showEditIcon ? 1 : 0,
            pointerEvents: showEditIcon ? 'auto' : 'none',
            transitionDelay: showEditIcon ? '100ms' : '0ms',
            transition: 'opacity 300ms linear',
            '&:hover': {
              backgroundColor: '#E6185A',
            },
          }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}

      {/* Edit icon */}
      <IconButton
        ref={editIconRef}
        size="small"
        onClick={e => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('editableblock:closeAll'));
          // capture icon position and open popover anchored to it
          try {
            const r = editIconRef.current && editIconRef.current.getBoundingClientRect();
            setAnchorRect(r || null);
          } catch (err) {
            setAnchorRect(null);
          }
          setIsPopoverOpen(true);
        }}
        sx={{
          position: 'absolute',
          ...iconPositionStyles,
          width: 28,
          height: 28,
          zIndex: 10,
          borderRadius: '50%',
          backgroundColor: theme.palette.primary.main,
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
          transform: 'scale(1)',
          opacity: showEditIcon ? 1 : 0,
          pointerEvents: showEditIcon ? 'auto' : 'none',
          transitionDelay: showEditIcon ? '100ms' : '0ms',
          transition: 'opacity 300ms linear',
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
          },
        }}
      >
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <StyleEditorPopover
        open={isPopoverOpen}
        anchorRect={anchorRect}
        title={editorTitle}
        style={style}
        fields={editorFields}
        onStyleChange={onStyleChange}
        onClose={() => setIsPopoverOpen(false)}
      />
    </Box>
  );
};

export default EditableBlock;
